// src/components/HeaderNotifications.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    IconButton,
    Badge,
    Popover,
    Typography,
    Button,
    CircularProgress,
    Divider,
    Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CircleIcon from '@mui/icons-material/Circle';
import axiosInstance from '../utils/axiosInstance';
import { usePanel, PanelType } from '../contexts/PanelContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

export default function HeaderNotifications({ panelColor }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { activePanel } = usePanel();

    const getPanelType = () => {
        switch (activePanel) {
            case PanelType.STUDENT: return 'student';
            case PanelType.CUSTOMER: return 'customer';
            default: return 'creator';
        }
    };

    const getViewAllPath = () => {
        switch (activePanel) {
            case PanelType.STUDENT:
                return '/student-portal/notifications';
            case PanelType.CUSTOMER:
                return '/customer-panel/notifications';
            default:
                return '/creator-panel/notifications';
        }
    };

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/notifications', {
                params: { view_filter: 'mine', limit: 5, page: 1, panel_type: getPanelType() },
            });
            const data = res.data;
            const items = data.data || data.notifications || [];
            setNotifications(items);
            const unread = items.filter((n) => !n.is_read && !n.read).length;
            setUnreadCount(data.unread_count ?? unread);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, [activePanel]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const handleOpen = (e) => {
        setAnchorEl(e.currentTarget);
        fetchNotifications();
    };

    const handleClose = () => setAnchorEl(null);

    const handleMarkAllRead = async () => {
        try {
            await axiosInstance.put(`/notifications/mark-all-read?panel_type=${getPanelType()}`);
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read: true })));
            setUnreadCount(0);
        } catch {
            // silent
        }
    };

    const handleViewAll = () => {
        handleClose();
        navigate(getViewAllPath());
    };

    const open = Boolean(anchorEl);

    return (
        <Box sx={{ mr: { xs: 0, md: 0.5 } }}>
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleOpen}
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        bgcolor: `${panelColor}14`,
                        border: `1px solid ${panelColor}26`,
                        '&:hover': { bgcolor: `${panelColor}1f` },
                    }}
                >
                    <Badge
                        badgeContent={unreadCount}
                        max={99}
                        sx={{
                            '& .MuiBadge-badge': {
                                bgcolor: '#ef4444',
                                color: '#fff',
                                fontSize: '0.55rem',
                                fontWeight: 700,
                                minWidth: 16,
                                height: 16,
                                top: -2,
                                right: -2,
                            },
                        }}
                    >
                        <NotificationsIcon sx={{ fontSize: '1.2rem', color: panelColor }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                marginThreshold={8}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100vw - 16px)', sm: 360 },
                        maxWidth: { xs: 320, sm: 360 },
                        maxHeight: { xs: '70vh', sm: 480 },
                        borderRadius: { xs: '10px', sm: '14px' },
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        mt: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 700, color: '#1e293b' }}>
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: panelColor, fontWeight: 600 }}>
                                {unreadCount} unread
                            </Typography>
                        )}
                    </Box>
                    {unreadCount > 0 && (
                        <Tooltip title="Mark all as read">
                            <IconButton size="small" onClick={handleMarkAllRead} sx={{ color: panelColor }}>
                                <DoneAllIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* List */}
                <Box sx={{
                    overflowY: 'auto',
                    flex: 1,
                    '&::-webkit-scrollbar': { width: '3px' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                }}>
                    {loading && notifications.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 3, sm: 4 } }}>
                            <CircularProgress size={24} sx={{ color: panelColor }} />
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: 2 }}>
                            <NotificationsIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#e2e8f0', mb: 1 }} />
                            <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: '#94a3b8' }}>
                                No notifications yet
                            </Typography>
                        </Box>
                    ) : (
                        notifications.map((n, idx) => {
                            const isRead = n.is_read || n.read;
                            return (
                                <Box key={n.id || n._id || idx}>
                                    <Box
                                        sx={{
                                            px: { xs: 1.5, sm: 2 },
                                            py: { xs: 1, sm: 1.5 },
                                            cursor: 'pointer',
                                            bgcolor: isRead ? 'transparent' : `${panelColor}08`,
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                                            display: 'flex',
                                            gap: { xs: 1, sm: 1.5 },
                                            alignItems: 'flex-start',
                                        }}
                                        onClick={handleViewAll}
                                    >
                                        {!isRead && (
                                            <CircleIcon sx={{ fontSize: 8, color: panelColor, mt: 0.8, flexShrink: 0 }} />
                                        )}
                                        <Box sx={{ flex: 1, ml: isRead ? 2.5 : 0, minWidth: 0 }}>
                                            <Typography sx={{
                                                fontFamily,
                                                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                                                fontWeight: isRead ? 500 : 700,
                                                color: '#1e293b',
                                                lineHeight: 1.3,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {n.title}
                                            </Typography>
                                            {(n.message || n.body) && (
                                                <Typography sx={{
                                                    fontFamily,
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    color: '#64748b',
                                                    lineHeight: 1.3,
                                                    mt: 0.25,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {n.message || n.body}
                                                </Typography>
                                            )}
                                            <Typography sx={{ fontFamily, fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: '#94a3b8', mt: 0.5 }}>
                                                {n.creator_name && `From ${n.creator_name} · `}
                                                {timeAgo(n.created_on || n.created_at || n.createdAt)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {idx < notifications.length - 1 && <Divider />}
                                </Box>
                            );
                        })
                    )}
                </Box>

                {/* Footer */}
                <Divider />
                <Box sx={{ p: { xs: 1, sm: 1.5 }, textAlign: 'center', flexShrink: 0 }}>
                    <Button
                        onClick={handleViewAll}
                        sx={{
                            fontFamily,
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            fontWeight: 600,
                            color: panelColor,
                            textTransform: 'none',
                            '&:hover': { bgcolor: `${panelColor}14` },
                        }}
                    >
                        View All Notifications
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
}
